// File: clientapp/src/pages/DocumentsPage.jsx
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Breadcrumbs,
    Link as MUILink,
    Typography,
    IconButton,
    Grid,
    Card,
    CardActionArea,
    CardContent
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HistoryIcon from '@mui/icons-material/History';
import BuildIcon from '@mui/icons-material/Build';
import BugReportIcon from '@mui/icons-material/BugReport';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { AuthContext } from '../contexts/AuthContext';
import CategoryIcon from '@mui/icons-material/Category';

const DocumentsPage = () => {
    const navigate = useNavigate();
    const { features } = useContext(AuthContext);

    // Feature access based on AuthContext
    const canUploadDocs = features && features.includes("Upload Docs for Classification");
    const canAdminModels = features?.includes("Administer Document Classification Models");
    const canAdminClassifications = features?.includes("Administer Document Classifications");
    const showAdminSection = canAdminModels || canAdminClassifications;
    

    // Navigation handlers for each card action.
    const goToUploadDocs = () => {
        navigate('/upload-docs'); // update route as needed
    };

    const goToDocHistory = () => {
        navigate('/doc-history'); // update route as needed
    };

    // Admin actions now use the routes from the old navbar.
    const goToTrainModels = () => {
        navigate('/training');
    };

    const goToTestModels = () => {
        navigate('/testing');
    };

    const goToTrainedModels = () => {
        navigate('/documentmodels');
    };

    // Close button navigates back.
    const handleClose = () => {
        navigate(-1);
    };

    return (
        <Container sx={{ mt: 2 }}>
            {/* Breadcrumb */}
            <Box sx={{ mb: 1 }}>
                <Breadcrumbs aria-label="breadcrumb">
                    <MUILink underline="hover" color="inherit" onClick={() => navigate('/')}>
                        Home
                    </MUILink>
                    <Typography color="text.primary">Documents</Typography>
                </Breadcrumbs>
            </Box>

            {/* Page Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {/* Changed from HomeIcon to a document icon */}
                    <InsertDriveFileIcon sx={{ fontSize: 48, mr: 1 }} />
                    <Box>
                        <Typography
                            variant="h4"
                            sx={{ fontWeight: 'bold', fontSize: 24 }}  // Set header font size to 24px
                        >
                            Documents
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            sx={{ color: 'text.secondary', fontSize: 12 }}  // Set subtitle font size to 12px
                        >
                            Manage your document uploads and history
                        </Typography>
                    </Box>
                </Box>
                {/* X to close */}
                <IconButton onClick={handleClose} sx={{ fontSize: 24 }}>
                    <CloseIcon sx={{ fontSize: 24 }} />
                </IconButton>
            </Box>

            {/* Action Tiles */}
            <Box sx={{ mb: 4 }}>
                <Grid container spacing={3}>
                    {canUploadDocs && (
                        <Grid item xs={12}>
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={4} md={3}>
                                    <Card>
                                        <CardActionArea onClick={goToUploadDocs}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                                                <CloudUploadIcon sx={{ fontSize: 48, color: '#B74140' }} />
                                                <CardContent>
                                                    <Typography variant="h6" align="center">
                                                        Upload Docs
                                                    </Typography>
                                                </CardContent>
                                            </Box>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={4} md={3}>
                                    <Card>
                                        <CardActionArea onClick={goToDocHistory}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                                                <HistoryIcon sx={{ fontSize: 48, color: '#B74140' }} />
                                                <CardContent>
                                                    <Typography variant="h6" align="center">
                                                        Doc History
                                                    </Typography>
                                                </CardContent>
                                            </Box>
                                        </CardActionArea>
                                    </Card>
                                </Grid>                                
                            </Grid>
                        </Grid>
                    )}

                    {/* Admin Section */}
                    {showAdminSection && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Admin
                            </Typography>
                            <Grid container spacing={2}>
                                {/* AI Model Training */}
                                {canAdminModels && (
                                    <Grid item xs={6} sm={4} md={3}>
                                        <Card>
                                            <CardActionArea onClick={goToTrainModels}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                                                    <BuildIcon sx={{ fontSize: 48, color: '#B74140' }} />
                                                    <CardContent>
                                                        <Typography variant="h6" align="center">
                                                            AI Model Training
                                                        </Typography>
                                                    </CardContent>
                                                </Box>
                                            </CardActionArea>
                                        </Card>
                                    </Grid>
                                )}
                                {/* AI Model Testing */}
                                {canAdminModels && (
                                    <Grid item xs={6} sm={4} md={3}>
                                        <Card>
                                            <CardActionArea onClick={goToTestModels}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                                                    <BugReportIcon sx={{ fontSize: 48, color: '#B74140' }} />
                                                    <CardContent>
                                                        <Typography variant="h6" align="center">
                                                            AI Model Testing
                                                        </Typography>
                                                    </CardContent>
                                                </Box>
                                            </CardActionArea>
                                        </Card>
                                    </Grid>
                                )}
                                {/* AI Trained Models */}
                                {canAdminModels && (
                                    <Grid item xs={6} sm={4} md={3}>
                                        <Card>
                                            <CardActionArea onClick={goToTrainedModels}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                                                    <DoneAllIcon sx={{ fontSize: 48, color: '#B74140' }} />
                                                    <CardContent>
                                                        <Typography variant="h6" align="center">
                                                            AI Trained Models
                                                        </Typography>
                                                    </CardContent>
                                                </Box>
                                            </CardActionArea>
                                        </Card>
                                    </Grid>
                                )}
                                {/* Document Classifications */}
                                {canAdminClassifications && (
                                    <Grid item xs={6} sm={4} md={3}>
                                        <Card>
                                            <CardActionArea onClick={() => navigate('/document-classifications')}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                                                    <CategoryIcon sx={{ fontSize: 48, color: '#B74140' }} />
                                                    <CardContent>
                                                        <Typography variant="h6" align="center">
                                                            Classifications
                                                        </Typography>
                                                    </CardContent>
                                                </Box>
                                            </CardActionArea>
                                        </Card>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </Container>
    );
};

export default DocumentsPage;
